import Vue from 'vue';
import { Commit } from 'vuex';
import { Room, RoomEdit } from '@/interfaces/lighting';

export interface RoomState {
	rooms: {
		[id: number]: Room;
	};
	selectedRoom: Room | null;
}

export default {
	namespaced: true,
	state: {
		rooms: [],
		selectedRoom: null,
	} as RoomState,
	getters: {
		getRooms: (state: RoomState): Room[] => {
			return Object.values(state.rooms);
		},

		getFloorRooms:
			(state: RoomState) =>
			(areaId: number): Room[] => {
				return Object.values(state.rooms).filter((room) => Number(room.fk_area) == areaId);
			},

		getRoom:
			(state: RoomState) =>
			(id: number): Room | undefined => {
				return state.rooms[id];
			},

		getSelectedRoom: (state: RoomState): Room | null => {
			return state.selectedRoom;
		},
	},
	mutations: {
		SET_ROOMS(state: RoomState, rooms: Room[]): void {
			const ret: { [id: number]: Room } = {};
			state.rooms = rooms.reduce((obj, room) => {
				obj[room.id] = room;
				return obj;
			}, ret);
		},

		ADD_ROOM(state: RoomState, room: Room): void {
			Vue.set(state.rooms, room.id, room);
		},

		CHANGE_ROOM(state: RoomState, room: Room & { id: number }): void {
			const oldRoom = state.rooms[room.id] || {};
			Vue.set(state.rooms, room.id, { ...oldRoom, ...room });
		},

		DELETE_ROOM(state: RoomState, id: number): void {
			Vue.delete(state.rooms, id);
		},

		CHANGE_SELECTED_ROOM(state: RoomState, room: Room): void {
			state.selectedRoom = room;
		},

		CLEAN_ROOM(state: RoomState): void {
			state.rooms = {};
		},

		CLEAN_SELECTED_ROOM(state: RoomState): void {
			state.selectedRoom = null;
		},
	},
	actions: {
		async fetchRooms({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/lighting/rooms`);

			if (response && response.status === 200) {
				commit('SET_ROOMS', response.data.data);
			}
		},

		async addRoom(
			{ commit }: { commit: Commit },
			{ payload }: { payload: RoomEdit }
		): Promise<void> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/lighting/rooms`,
				payload
			);
			if (response && response.status === 201) {
				commit('ADD_ROOM', response.data.data);
			}
		},

		async modifyRoom(
			{ commit }: { commit: Commit },
			{ id, payload }: { id: number; payload: RoomEdit }
		): Promise<void> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/lighting/rooms/${id}`,
				payload
			);
			if (response && response.status === 200) {
				commit('CHANGE_ROOM', response.data.data);
			}
		},

		async deleteRoom({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/lighting/rooms/${id}`
			);
			if (response && response.status === 200) {
				commit('DELETE_ROOM', id);
			}
		},

		selectRoom({ commit }: { commit: Commit }, { room }: { room: Room }): void {
			if (room) {
				commit('CHANGE_SELECTED_ROOM', room);
			}
		},

		cleanRoom({ commit }: { commit: Commit }): void {
			commit('CLEAN_ROOM');
		},

		cleanSelectedRoom({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_ROOM');
		},

		cleanAllRoom({ commit }: { commit: Commit }): void {
			commit('CLEAN_ROOM');
			commit('CLEAN_SELECTED_ROOM');
		},
	},
};
